import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Map, LifeBuoy } from 'lucide-react';

/**
 * Shared 3-item bottom bar (Dashboard, Journey, Help).
 *
 * The Dashboard page renders its own 6-item bar directly, so this shared
 * bar must NOT render on /dashboard: otherwise the two fixed footers
 * stack at the same z-index and the one at the top of the stack blocks
 * taps on Resources, Inbox and RR. The hidden-paths list keeps this bar
 * off auth/landing/pin screens too.
 *
 * Active-screen highlighting (Batch 6 A6, Tudor 11 Feb 2026): on every
 * bar the current screen's label and icon are #ffcd04 and bold. The
 * theme-4a.css rules match any Footer.js nav button that carries the
 * `data-arp-active` attribute.
 */
function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  // Pages that render no bottom bar at all. /dashboard is now included
  // because the Dashboard page renders its own 6-item bar.
  const hidden = [
    '/',
    '/landing',
    '/login',
    '/reset-password',
    '/set-pin',
    '/dashboard',
  ];
  if (hidden.includes(location.pathname)) {
    return null;
  }

  const path = location.pathname;
  const isDashboard = path === '/dashboard';
  const isJourney = path === '/journey' || path.startsWith('/journey/');
  const isHelp = path === '/get-help' || path.startsWith('/get-help');

  return (
    <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-blue-200 h-14 flex items-center justify-center z-30 shadow-lg">
      <div className="flex items-center space-x-6 sm:space-x-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-2 px-3 py-2 hover:bg-blue-50 rounded-md transition-colors"
          data-testid="footer-dashboard-link"
          data-arp-active={isDashboard ? 'true' : undefined}
        >
          <Home className="w-5 h-5 text-blue-600" strokeWidth={2} />
          <span className="text-sm font-medium text-blue-700 uppercase tracking-wider">Dashboard</span>
        </button>
        <button
          onClick={() => navigate('/journey')}
          className="flex items-center space-x-2 px-3 py-2 hover:bg-blue-50 rounded-md transition-colors"
          data-testid="footer-journey-link"
          data-arp-active={isJourney ? 'true' : undefined}
        >
          <Map className="w-5 h-5 text-blue-600" strokeWidth={2} />
          <span className="text-sm font-medium text-blue-700 uppercase tracking-wider">Journey</span>
        </button>
        {/* Feb 2026: permanent, ungated crisis-help link: matches the pattern
            already used for the verification explainer.  Visible to every
            user at all times, not gated behind login or quiz completion. */}
        <button
          onClick={() => navigate('/get-help')}
          className="flex items-center space-x-2 px-3 py-2 hover:bg-red-50 rounded-md transition-colors"
          data-testid="footer-crisis-help-link"
          data-arp-active={isHelp ? 'true' : undefined}
          aria-label="Crisis help resources"
          title="Crisis help resources"
        >
          <LifeBuoy className="w-5 h-5 text-red-600" strokeWidth={2} />
          <span className="text-sm font-medium text-red-700 uppercase tracking-wider">Help</span>
        </button>
      </div>
    </footer>
  );
}

export default Footer;
